import classNames from 'classnames';
import { isEqual } from 'lodash/fp';
import { divWithClassName } from '../../../services/Component';
import Autocomplete from '../../Autocomplete';
import Checkbox from '../../Checkbox';
import CheckboxContainer from '../../CheckboxContainer';
import DatePicker from '../../DateTime/DatePicker';
import TimePicker from '../../DateTime/TimePicker';
import { GridRadio } from '../../GridRadio';
import Input from '../../Input';
import InputError from '../../Input/InputError';
import { RadioButtonPlain } from '../../RadioButton';
import { RadioButtonContainerPlain } from '../../RadioButtonContainer';
import RichTextArea from '../../RichTextArea';
import { Select } from '../../Select';
import SelectSimple from '../../SelectSimple';
import TextArea from '../../TextArea';
import TextareaAutosize from '../../TextareaAutosize';
import Toggle from '../../Toggle';
import styles from './style.m.less';

/** Just a div with some margins/padding */
export const FormInputWrapper = divWithClassName(styles.formInputWrapper);

/** A wrapper around `Input` that prepends a class that adds padding to `input` */
export const FormInput = ({ className = '', ...props }) => (
    <Input className={classNames(styles.formInput, className)} {...props} />
);

export const FormInputWithWrapper = ({ ...props }) => (
    <FormInputWrapper>
        <FormInput {...props} />
    </FormInputWrapper>
);

export const FormCheckboxContainer = props => (
    <FormInputWrapper>
        <CheckboxContainer {...props} />
    </FormInputWrapper>
);

export const FormCheckbox = ({ value, ...otherProps }) => (
    <Checkbox checked={value} className={styles.formCheckbox} {...otherProps} />
);

export const FormRadioButtonContainer = props => (
    <FormInputWrapper>
        <RadioButtonContainerPlain {...props} />
    </FormInputWrapper>
);

export const FormRadioButton = props => <RadioButtonPlain {...props} />;

export const FormRichTextArea = ({ error, ...otherProps }) => (
    <FormInputWrapper>
        <RichTextArea {...otherProps} className={styles.formRichTextArea} invalid={!!error} />
        <InputError error={error} />
    </FormInputWrapper>
);

export const FormSelect = ({ error = undefined, value, onChange, wrapperClassName, ...otherProps }) => (
    <FormInputWrapper className={wrapperClassName}>
        <Select hasError={!!error} {...otherProps} selected={isEqual(value, '-') ? null : value} onSelect={onChange} />
        <InputError error={error} />
    </FormInputWrapper>
);

export const FormSelectSimple = ({ error = undefined, value, wrapperClassName, onChange, ...otherProps }) => (
    <FormInputWrapper className={wrapperClassName}>
        <SelectSimple
            value={value}
            onChange={e => {
                onChange(e.target.value);
            }}
            hasError={!!error}
            className={styles.selectSimple}
            {...otherProps}
        />
        <InputError error={error} />
    </FormInputWrapper>
);

export const FormGridRadio = ({
    error = undefined,
    options,
    value,
    wrapperClassName = '',
    onChange,
    url,
    ...otherProps
}) => (
    <FormInputWrapper className={wrapperClassName}>
        <GridRadio options={options} value={value} onChange={onChange} url={url} {...otherProps} />
        <InputError error={error} />
    </FormInputWrapper>
);

export const FormButtonContainer = divWithClassName(classNames('button-container', styles.formButtonContainer));

export const FormDatePicker = ({ value, error, wrapperClassName = '', ...props }) => {
    return (
        <FormInputWrapper className={wrapperClassName}>
            <DatePicker {...props} date={value} className={styles.formInput} hasError={!!error} />
        </FormInputWrapper>
    );
};

export const FormTextArea = ({ ...otherProps }) => (
    <FormInputWrapper>
        <TextArea {...otherProps} />
    </FormInputWrapper>
);

export const FormTextareaAutosize = props => (
    <FormInputWrapper>
        <TextareaAutosize className={styles.formTextareaAutosize} {...props} />
    </FormInputWrapper>
);

export const FormSentence = ({ children }) => <div className={styles.formSentence}>{children}</div>;

export const FormSentenceInput = props => <Input className={styles.formSentenceInput} {...props} />;
export const FormSentenceSelect = ({ value, onChange, ...props }) => (
    <Select className={styles.formSentenceInput} selected={value} onSelect={onChange} {...props} />
);

export const FormToggle = ({ checked, wrapperClassName = '', ...otherProps }) => {
    return (
        <FormInputWrapper className={wrapperClassName}>
            <Toggle checked={checked} {...otherProps} />
        </FormInputWrapper>
    );
};

export const FormAutocomplete = ({ value, onChange, ...otherProps }) => (
    <Autocomplete value={value} onSelect={onChange} className={styles.formInput} {...otherProps} />
);

export const FormTimePicker = ({ wrapperClassName = '', ...props }) => {
    return (
        <FormInputWrapper className={wrapperClassName}>
            <TimePicker {...props} />
        </FormInputWrapper>
    );
};
